export interface MetaOptions {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

export interface IMetaService {
  updateMeta(options?: MetaOptions): void;
}

export class MetaService implements IMetaService {
  private defaultTitle = 'Geireann Lindfield Roberts — Senior Software Engineer';
  private defaultDescription = 'Senior Software Engineer focused on AI image editing and model harness infrastructure at Adobe Firefly.';
  private defaultUrl = 'https://www.geireann.com';

  public updateMeta(options: MetaOptions = {}): void {
    if (typeof document === 'undefined') return;

    const title = options.title ? `${options.title} — Geireann Lindfield Roberts` : this.defaultTitle;
    const description = options.description || this.defaultDescription;
    const url = options.url || (typeof window !== 'undefined' ? window.location.href : this.defaultUrl);

    // Update document title
    document.title = title;

    // Update meta description
    this.setMetaTag('name', 'description', description);

    // Update Open Graph tags
    this.setMetaTag('property', 'og:title', title);
    this.setMetaTag('property', 'og:description', description);
    this.setMetaTag('property', 'og:url', url);

    // Update Twitter Card tags
    this.setMetaTag('name', 'twitter:title', title);
    this.setMetaTag('name', 'twitter:description', description);

    // Image handling: Only include og:image and summary_large_image if explicit image is provided
    if (options.image) {
      const image = this.resolveImageUrl(options.image);
      this.setMetaTag('property', 'og:image', image);
      this.setMetaTag('name', 'twitter:image', image);
      this.setMetaTag('name', 'twitter:card', 'summary_large_image');
    } else {
      this.removeMetaTag('property', 'og:image');
      this.removeMetaTag('name', 'twitter:image');
      this.setMetaTag('name', 'twitter:card', 'summary');
    }
  }

  private resolveImageUrl(path: string): string {
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `https://www.geireann.com${cleanPath}`;
  }

  private setMetaTag(attrName: 'name' | 'property', attrValue: string, content: string): void {
    let element = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attrName, attrValue);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  }

  private removeMetaTag(attrName: 'name' | 'property', attrValue: string): void {
    const element = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (element) {
      element.remove();
    }
  }
}

export const metaService = new MetaService();
